import Error404 from "../components/404";
import Error429 from "../components/429";
import LayoutError from "../components/maintenance/LayoutError";
import {getLayoutDirection, LinguiClientProvider} from "../locales";
import Head from "next/head";
import dynamic from "next/dynamic";
import {buildTokensCss} from "../themes/Campagne/tokens";
import {isCampagneV2} from "../themes/Campagne/V2/detect";
import React from "react";
import {ConfigProvider} from "antd";
import logger from "./logger";

// Chaque thème est chargé dynamiquement → Webpack lui alloue son PROPRE chunk JS.
// Un visiteur ne télécharge QUE le thème réellement rendu (au lieu des 5 empaquetés
// ensemble). Le SSR reste actif (pas de `ssr:false`) : le HTML serveur du thème est
// rendu normalement (SEO préservé), seul le JS client est splitté. `loading:()=>null`
// car le HTML SSR est déjà là → pas de flash pendant l'hydratation du chunk.
// `buildTokensCss` (utilitaire Campagne, 0 dépendance) reste en import statique.
//
// ⚠️ CHAQUE appel doit être un LITTÉRAL `dynamic(() => import("..."), {...})` —
// PAS de wrapper/factory du genre `const dynTheme = (loader) => dynamic(loader, …)`.
// La transformation SWC `next-dynamic` ne reconnaît que la forme littérale : c'est
// elle qui injecte les options `modules`/`webpack` permettant au SSR d'émettre les
// `dynamicIds` et au client de PRÉCHARGER le chunk du thème AVANT l'hydratation.
// Avec un wrapper, pas de préchargement → le client rend `null` (fallback) là où le
// HTML SSR contient le thème → erreurs d'hydratation (« Did not expect server HTML
// to contain a <style> in <div> »), systématiques en dev après chaque recompilation.
// (Un import statique en dev n'est pas possible : les thèmes sont des modules async
// webpack — top-level await dans leur graphe — donc require() renvoie un objet vide.)
const ThemeUmmah      = dynamic(() => import("../themes/Ummah"), { loading: () => null });
const Scrollbar       = dynamic(() => import("../themes/Ummah/theme/components/scrollbar"), { loading: () => null });
const ThemeCampagne   = dynamic(() => import("../themes/Campagne"), { loading: () => null });
const ThemeTravel     = dynamic(() => import("../themes/Travel"), { loading: () => null });
const ThemeTravel2    = dynamic(() => import("../themes/Travel-2"), { loading: () => null });
const Theme2026       = dynamic(() => import("../themes/2026"), { loading: () => null });
const Theme2026Mobile = dynamic(() => import("../themes/2026-mobile"), { loading: () => null });
// Billets QR (page.type "TICKETS") : rendus HORS thème, identiques sur tous les sites.
const TicketsPage     = dynamic(() => import("../components/Tickets/TicketsPage"), { loading: () => null });
// Contrôle d'accès d'un bénévole (lien temporaire, page.type "ACCESS_CONTROL"), hors thème lui aussi.
const AccessControlPage = dynamic(() => import("../components/Tickets/AccessControlPage"), { loading: () => null });


/**
 * Synchronise <html dir> et <html lang> avec la langue résolue (props.langValue).
 *
 * POURQUOI : `_document` ne s'exécute qu'au SSR, et son calcul de langue est
 * fragile (la langue vit dans le PATH `/slug/he/...`, pas dans `?lang=`, donc
 * `ctx.req.__NEXT_INIT_QUERY.lang` est vide → fallback "fr" → dir="ltr").
 * Résultat : `<html dir>` restait "ltr" même en hébreu, alors que TOUT le RTL
 * des thèmes en dépend — le CSS `[dir="rtl"] .tr-...` et le JS des Header qui
 * lit `document.documentElement.getAttribute("dir")`.
 *
 * `props.langValue` est, lui, la source de vérité (posé par RouterService).
 * Cet effet client couvre à la fois le SSR (corrige un éventuel dir erroné dès
 * l'hydratation) et toute navigation. Idempotent, aucun re-render React.
 */
function HtmlDirSync({ langValue }){
    React.useEffect(() => {
        if(typeof document === "undefined" || !langValue) return;
        const el = document.documentElement;
        const dir = getLayoutDirection(langValue);
        if(el.getAttribute("dir") !== dir) el.setAttribute("dir", dir);
        if(el.getAttribute("lang") !== langValue) el.setAttribute("lang", langValue);
    }, [langValue]);
    return null;
}


export default function RouterPage(p){

    let props={...p}

    logger.debug({ route: props.route }, "RouterPage route")

    if(props.errorPage)
    {
        switch (props.errorPage)
        {
            case "404":
                return <Error404/>
            case "429":
                return <Error429 retryAfter={props.retryAfter}/>
        }
    }

    if(props.page==null){
        props.page={type:"404"}
    }

    logger.debug({ theme: props.config.theme }, "RouterPage theme")

    if(props.page?.type === "TICKETS" || props.page?.type === "ACCESS_CONTROL"){
        return <TicketsStructure {...props}/>
    }

    switch (props.config.theme)
    {
        case "TRAVEL":
            return <TravelThemeStructure {...props}/>
        case "TRAVEL2":
            return <Travel2ThemeStructure {...props}/>
        case "CAMPAGNE":
            return <CampagneThemeStructure {...props}/>
        case "2026":
            return <Theme2026Structure {...props}/>
        case "2026-MOBILE":
            return <Theme2026MobileStructure {...props}/>
        default:
            return <UmmahThemeStructure {...props}/>
    }

}


// Page d'un visiteur à l'entrée d'un événement : son téléphone, ses billets. Pas de
// chrome de thème (menu, pied de page) : seulement le QR, lisible et plein écran.
function TicketsStructure(props)
{
    return (
        <LayoutError context={props}>
            <HtmlDirSync langValue={props.langValue}/>
            <LinguiClientProvider locale={props.langValue}>
                {props.page?.type === "ACCESS_CONTROL" ? <AccessControlPage context={props}/> : <TicketsPage context={props}/>}
            </LinguiClientProvider>
        </LayoutError>
    )
}


function Theme2026Structure(props)
{
    // Couleurs depuis config.theme2026.tokens (gérées en CSS-in-JS via ThemeVars,
    // mais on les expose aussi à Ant Design pour cohérence des composants Antd
    // intégrés dans les pages 2026, ex: Newsletter inscription notification).
    const tokens = props.config?.theme2026?.tokens || {}
    const antdTheme = {}
    if(tokens.primary) antdTheme.colorPrimary = tokens.primary
    if(tokens.secondary) antdTheme.colorSecondary = tokens.secondary

    return (
        <LayoutError context={props}>
            <Head>
                {/* CSS 2026 autonome : uniquement les classes .tr-* utilisées par 2026,
                    scopées .theme-2026, sans aucun sélecteur global sur balises HTML.
                    Pas de travel.css ni bootstrap pour ne pas polluer le contenu HTML libre. */}
                <link rel={"stylesheet"} href={"/ummah/css/bootstrap.min.css"}/>
                <link rel={"stylesheet"} href={"/2026/css/2026.css"}/>
            </Head>
            <div className="App" id='scrool'>
                <LinguiClientProvider locale={props.langValue}>
                    <ConfigProvider
                        locale={props.langValue}
                        direction={getLayoutDirection(props.langValue)}
                        theme={{ token: { ...antdTheme } }}
                    >
                        <Theme2026 context={props}/>
                    </ConfigProvider>
                </LinguiClientProvider>
            </div>
        </LayoutError>
    )
}


// Thème de l'app mobile marque blanche. Dérivé de 2026 (copie indépendante,
// themes/2026-mobile) : diverge librement (thème propre, pages spéciales,
// onboarding). Sélectionné quand le calque mobile pose props.config.theme =
// "2026-MOBILE" (cookie mobile=1), ou en forçant ?theme=2026-MOBILE.
function Theme2026MobileStructure(props)
{
    const tokens = props.config?.theme2026?.tokens || {}
    const antdTheme = {}
    if(tokens.primary) antdTheme.colorPrimary = tokens.primary
    if(tokens.secondary) antdTheme.colorSecondary = tokens.secondary

    return (
        <LayoutError context={props}>
            <Head>
                <link rel={"stylesheet"} href={"/ummah/css/bootstrap.min.css"}/>
                <link rel={"stylesheet"} href={"/2026-mobile/css/2026.css"}/>
            </Head>
            <div className="App" id='scrool'>
                <LinguiClientProvider locale={props.langValue}>
                    <ConfigProvider
                        locale={props.langValue}
                        direction={getLayoutDirection(props.langValue)}
                        theme={{ token: { ...antdTheme } }}
                    >
                        <Theme2026Mobile context={props}/>
                    </ConfigProvider>
                </LinguiClientProvider>
            </div>
        </LayoutError>
    )
}



function UmmahThemeStructure(props)
{

    return (
        <LayoutError context={props}>
            <Head>
                <script src="https://cdn.tailwindcss.com"></script>

                <link rel={"stylesheet"} href={"/ummah/css/font-awesome.min.css"}/>
                <link rel={"stylesheet"} href={"/ummah/css/themify-icons.css"}/>
                <link rel={"stylesheet"} href={"/ummah/css/flaticon.css"}/>
                <link rel={"stylesheet"} href={"/ummah/css/bootstrap.min.css"}/>
                <link rel={"stylesheet"} href={"/ummah/css/ummah.all.css"}/>

            </Head>
            <div className="App unisoft-ummah" id='scrool'>
                <LinguiClientProvider locale={props.langValue}>
                    <ThemeUmmah context={props}/>
                    <Scrollbar/>
                </LinguiClientProvider>
            </div>
        </LayoutError>
    )
}


function TravelThemeStructure(props)
{

    let antdTheme={}

    if(props.org?.travel?.theme?.primaryColor){
        antdTheme.colorPrimary=props.org?.travel?.theme?.primaryColor
    }

    if(props.org?.travel?.theme?.secondaryColor){
        antdTheme.colorSecondary=props.org?.travel?.theme?.secondaryColor
    }

    return (
        <LayoutError context={props}>
            <Head>
                <link rel={"stylesheet"} href={"/ummah/css/bootstrap.min.css"}/>
                <link rel={"stylesheet"} href={"/travel/css/travel.css"}/>
            </Head>
            <div className="App" id='scrool'>
                <HtmlDirSync langValue={props.langValue}/>
                <LinguiClientProvider locale={props.langValue}>
                    <ConfigProvider
                        locale={props.langValue}
                        direction={getLayoutDirection(props.langValue)}
                        theme={{
                            token: {
                                ...antdTheme,
                            },
                        }}
                    >
                        <ThemeTravel context={props}/>
                    </ConfigProvider>
                </LinguiClientProvider>
            </div>
        </LayoutError>
    )
}

// Deuxième thème travel (design "Panama"). Copie indépendante de Travel
// (themes/Travel-2 + /travel-2/css/travel.css) : consomme EXACTEMENT la même
// config/data (org.travel) mais diverge visuellement. Sélectionné quand une org
// travel pose travel.themeKey = "TRAVEL2" (cf. RouterService), ou ?theme=TRAVEL2.
function Travel2ThemeStructure(props)
{

    let antdTheme={}

    if(props.org?.travel?.theme?.primaryColor){
        antdTheme.colorPrimary=props.org?.travel?.theme?.primaryColor
    }

    if(props.org?.travel?.theme?.secondaryColor){
        antdTheme.colorSecondary=props.org?.travel?.theme?.secondaryColor
    }

    return (
        <LayoutError context={props}>
            <Head>
                <link rel={"stylesheet"} href={"/ummah/css/bootstrap.min.css"}/>
                <link rel={"stylesheet"} href={"/travel-2/css/travel.css"}/>
            </Head>
            <div className="App" id='scrool'>
                <HtmlDirSync langValue={props.langValue}/>
                <LinguiClientProvider locale={props.langValue}>
                    <ConfigProvider
                        locale={props.langValue}
                        direction={getLayoutDirection(props.langValue)}
                        theme={{
                            token: {
                                ...antdTheme,
                            },
                        }}
                    >
                        <ThemeTravel2 context={props}/>
                    </ConfigProvider>
                </LinguiClientProvider>
            </div>
        </LayoutError>
    )
}

function CampagneThemeStructure(props)
{
    const campagneConfig = props?.data?.[0]?.config

    // Page de campagne v2 : aucune feuille globale (ni Bootstrap, ni Font Awesome, ni
    // anciennes feuilles de campagne), une seule police. Les écrans live (/__screen) et
    // la page de paiement gardent strictement l'ancien chargement.
    const isScreenRoute = Array.isArray(props?.route) && String(props.route[0] || "").startsWith("__screen")
    if(props?.page?.type === "CAMPAGNE" && !isScreenRoute && isCampagneV2(props?.data?.[0], props?.query)){
        return (
            <LayoutError context={props}>
                {/* La feuille de la police est déclarée dans le module CSS de la V2
                    (`themes/Campagne/V2/v2.module.css`) : passer une feuille de style par
                    `next/head` déclenche un avertissement Next.js à chaque rendu. Ici, on
                    ne garde que les préconnexions, qui préparent la requête. */}
                <Head>
                    <link rel="preconnect" href="https://fonts.googleapis.com"/>
                    <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin=""/>
                </Head>
                <LinguiClientProvider locale={props.langValue}>
                    <ThemeCampagne context={props}/>
                </LinguiClientProvider>
            </LayoutError>
        )
    }

    // Nouveau système : si config.tokens est défini → on charge le CSS unique
    // campagne.css et on injecte les overrides via <style>. Sinon legacy.
    const tokens = campagneConfig?.tokens
    const useTokens = Boolean(tokens && (tokens.main || tokens.custom))
    const tokensCss = useTokens ? buildTokensCss(tokens) : ""

    // Legacy : résolution du nom de fichier CSS via template/theme/secondaryThemes
    let theme = "blue"
    if(campagneConfig?.template === "ALD"){
        theme = "ald"
    }
    if(campagneConfig?.theme){
        theme = campagneConfig.theme
    }

    let secondaryThemes = campagneConfig?.secondaryThemes || []

    logger.debug({ css: useTokens ? "campagne.css (tokens)" : theme }, "CampagneTheme")

    return (
        <LayoutError context={props}>
            <Head>
                <link rel="preconnect" href="https://fonts.googleapis.com"/>
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin=""/>
                <link
                    href="https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,300;1,400;1,500;1,600;1,700;1,800;1,900&amp;display=swap"
                    rel="stylesheet"/>
                <link
                    href="https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap"
                    rel="stylesheet"/>

                <link rel="stylesheet"
                      href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css"
                      integrity="sha512-DTOQO9RWCH3ppGqcWaEA1BIZOC6xxalwEsw9c2QQeAIftl+Vegovlnee1c9QX4TctnWMn13TZye+giMm8e2LwA=="
                      crossOrigin="anonymous" referrerpolicy="no-referrer"/>
                <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"
                      rel="stylesheet"
                      integrity="sha384-T3c6CoIi6uLrA9TneNEoa7RxnatzjcDSCmG1MXxSR1GAsXEV/Dwwykc2MPK8M2HN"
                      crossOrigin="anonymous"/>

                {useTokens ? (
                    <link rel={"stylesheet"} href={"/campagne/css/campagne.css"}/>
                ) : (
                    <link rel={"stylesheet"} href={"/campagne/css/" + theme + ".css"}/>
                )}

                {!useTokens && secondaryThemes.map(t => {
                    return (
                        <link rel={"stylesheet"} href={"/campagne/css/" + t + ".css"}/>
                    )
                })}

                {tokensCss && (
                    <style dangerouslySetInnerHTML={{__html: tokensCss}}/>
                )}

            </Head>

            <LinguiClientProvider locale={props.langValue}>
                <ThemeCampagne context={props}/>
            </LinguiClientProvider>

            <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/js/bootstrap.bundle.min.js"
                    integrity="sha384-C6RzsynM9kWDrMNeT87bh95OGNyZPhcTNXj1NW7RuBCsyN/o0jlpcV8Qyq46cDfL"
                    crossOrigin="anonymous"></script>
        </LayoutError>
    )
}
